import React, { useContext, useEffect, useState } from 'react';
import {
    Row,
    Col,
    Form,
    Button,
    Grid,
    Input,
    Select,
    Space,
    Pagination,
    message,
} from 'antd';

import { ExportOutlined, SearchOutlined } from '@ant-design/icons';
import { ModalAddPlanogramFromProduct } from '@/components/ModalAddPlanogramFromProduct';
import * as S from './style';
import { topTitleContext } from '../../contexts/TopTitle';
import { useGetAllStore } from '../../store/Store';

import { print } from './TemplateHtmlPlanogram';

import {
    planogramColumn,
    systemColumn,
    expectedMetricsColumn,
    currentMetricsColumns,
} from './columns';
import { Planogram } from '../../store/Planogram/findAll';
import { PlanogramConfirm } from '../../components/Modals/PlanogramConfirm';
import { useUpdatePlanogramMutation } from '../../store/Planogram/update';
import { useExportPlanograms } from '../../store/Planogram/export';
import { exportPlanogramList } from './exportPlanogram';
import { useGetCategory } from '../../store/category';
import {
    PlanogramAutomationSteps,
    PlanogramUI,
    usePlanogramAutomationContext,
} from './context/PlanogramAutomation';
import { ModalPlanogram } from './_components/Modal';
import {
    PlanogramTableBlock,
    PlanogramTableType,
} from './_components/Tables/PlanogramData';
import { useConfirmExit } from './hooks/useConfirmExit';

interface PlanogramListExport {
    product_name: string;
    standard_amount: number;
    stock_amount: number;
    bar_code: string;
    amount_to_transfer: number;
}

const PlanogramAutomationScreen = () => {
    const { titleValue } = useContext(topTitleContext);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [storesSelected, setStoresSelected] = useState<Array<never>>([]);
    const [categorySelected, setCategorySelected] = useState<Array<never>>([]);

    const [loadingType, setLoadingType] = useState<'EXCELL' | 'PDF'>();

    const [openModal, setOpenModal] = useState(false);
    const [openModalSavePlanogram, setOpenModalSavePlanogram] = useState(false);

    const [
        openModalAddPlanogramFromProduct,
        setOpenModalAddPlanogramFromProduct,
    ] = useState(false);

    const {
        planogramList,
        isLoadingPlanogram,
        params,
        setParams,
        refetch,
        isFetching,
        planograms,
        planogramsCopy,
        planogramChecked,
        setPlanogramsChecked,
        planogramsUpdated,
        setPlanogramsUpdated,
        setCurrentStep,
    } = usePlanogramAutomationContext();

    const [meta, setMeta] = useState({
        page: 1,
        last_page: 1,
        total: 0,
        perPage: 10,
    });
    const [editTable, setEditTable] = useState(false);

    const { data: categoryList } = useGetCategory();

    const categoryOptions = categoryList?.map((category) => ({
        label: category.name,
        value: `${category.id}`,
    }));

    useConfirmExit(planogramsUpdated.length > 0);

    const {
        isLoading: isLoadingExportPlanogram,
        mutateAsync: mutateAsyncExportPlanogram,
    } = useExportPlanograms(params);

    const { mutateAsync, isLoading: isLoadingUpdatedPlanogram } =
        useUpdatePlanogramMutation();

    const [form] = Form.useForm();
    const [formplanogram] = Form.useForm();
    const [formPlanogramCreate] = Form.useForm();
    const breakpoints = Grid.useBreakpoint();
    const filtersHorizontalGutter = breakpoints.md ? 24 : 0;

    const confirmPlanogramChange = Form.useWatch('confirm', formplanogram);

    const { data: stores, isLoading } = useGetAllStore();

    const storesOptions = stores?.map((store) => ({
        label: store.name,
        value: `${store.id}`,
    }));

    const onSelectChange = (newSelectedRowKeys: any) => {
        setSelectedRowKeys(newSelectedRowKeys);
        let planogramsSavedAsMarked: PlanogramUI[] = [...planogramChecked];

        for (let i = 0; i < newSelectedRowKeys.length; i += 1) {
            const findPlanogram = planograms.find(
                (item) => item.id === newSelectedRowKeys[i],
            );
            const planogramExistInArray = planogramsSavedAsMarked.find(
                (item) => item.id === newSelectedRowKeys[i],
            );

            if (!findPlanogram) {
                // eslint-disable-next-line no-continue
                continue;
            }

            if (findPlanogram && !planogramExistInArray) {
                planogramsSavedAsMarked.push({
                    ...findPlanogram,
                    deleted: false,
                    edited: false,
                });
            }
        }

        if (newSelectedRowKeys.length !== planogramsSavedAsMarked.length) {
            const removedLines = planogramsSavedAsMarked.filter((item) => {
                if (newSelectedRowKeys.includes(item.id as never)) {
                    return item;
                }
            });
            planogramsSavedAsMarked = removedLines;
        }
        setPlanogramsChecked(planogramsSavedAsMarked);
    };

    const selectRow = (record: Planogram) => {
        const selectedRowKeysCopy = [...selectedRowKeys];
        if (selectedRowKeysCopy.indexOf(record.id as never) >= 0) {
            selectedRowKeysCopy.splice(
                selectedRowKeysCopy.indexOf(record.id as never),
                1,
            );
        } else {
            selectedRowKeysCopy.push(record.id as never);
        }
        onSelectChange(selectedRowKeysCopy);
        setSelectedRowKeys(selectedRowKeysCopy);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: 0,
        preserveSelectedRowKeys: true,
        selectionRenderer: () => null,
    };

    const rowSelectionFirstTable = {
        selectedRowKeys,
        onChange: onSelectChange,
        preserveSelectedRowKeys: true,
    };

    const filterOption = (
        input: string,
        option?: { label: string; value: string },
    ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

    const setCurrentPage = (value: number, pzSize: number) => {
        setMeta((prev) => {
            return { ...prev, page: value, perPage: pzSize };
        });
        setParams({
            ...params,
            page: value,
            perPage: pzSize,
        });
    };

    const onSubmit = (data: any) => {
        setParams({
            ...params,
            'filters[stores][]': storesSelected,
            'filters[categories][]': categorySelected,
            'filters[barCode]': data?.barcode,
            'filters[product]': data?.product,
            page: 1,
        });
        setSelectedRowKeys([]);
    };

    const onSavePlanogram = async () => {
        try {
            let checkPlanogramUpdated = planogramsUpdated;
            if (!planogramsUpdated.length) {
                setOpenModalSavePlanogram(false);
                message.warning(
                    'Nenhum planograma foi modificado, tente novamente.',
                );
                return;
            }
            if (planogramsUpdated.length !== selectedRowKeys.length) {
                const planogramsToBeSaved = checkPlanogramUpdated.filter(
                    (item) => selectedRowKeys.includes(item.id as never),
                );
                checkPlanogramUpdated = planogramsToBeSaved;
            }
            /**
             * transform lines
             */
            const updateLinesWithoutAfter = checkPlanogramUpdated.map(
                (item) => ({
                    ...item,
                    price_final_in_cents:
                        item.price_final_in_cents_after ||
                        item.price_final_in_cents,
                    price_list_in_cents:
                        item.price_list_in_cents_after ||
                        item.price_list_in_cents,
                }),
            );

            /**
             * Remove line that should not be sent to update
             */
            const updateLinesWithoutAfterAndRemovedLines =
                updateLinesWithoutAfter.filter((item) => {
                    if (item.should_remove_line) {
                        return false;
                    }

                    return true;
                });
            setOpenModalSavePlanogram(false);
            await mutateAsync(updateLinesWithoutAfterAndRemovedLines);
            setSelectedRowKeys([]);
            setPlanogramsUpdated([]);
            setPlanogramsChecked([]);
            setCurrentStep(PlanogramAutomationSteps.products);
            message.success('Planograma Atualizado');
            formplanogram.resetFields();
            setEditTable(false);
            refetch();
        } catch (error) {
            setEditTable(false);
            message.error(
                'Aconteceu algum problema. Tente novamente mais tarde',
            );
        }
    };

    useEffect(() => {
        titleValue('Automação de Planograma');
    }, []);

    useEffect(() => {
        if (planogramList) {
            setMeta({
                ...meta,
                page: planogramList.meta.current_page,
                total: planogramList.meta.total,
                last_page: planogramList.meta.last_page,
            });
        }
    }, [planogramList?.data, isFetching]);

    useEffect(() => {
        const keys = planogramChecked.map((item) => item.id);
        setSelectedRowKeys(keys);
    }, [planogramChecked]);

    const rowClassName = (record: any) => {
        const stylesRow =
            record.deleted && record.edited
                ? `pointed deleted edited`
                : record.edited
                ? 'pointed edited'
                : 'pointed';
        return stylesRow;
    };

    const calculateItemsForExport = (
        response: Planogram[],
    ): PlanogramListExport[] => {
        const sumItems: { [productName: string]: PlanogramListExport } = {};

        response.forEach((item) => {
            const productName = item.product.name;
            if (!sumItems[productName]) {
                sumItems[productName] = {
                    product_name: productName,
                    standard_amount: 0,
                    stock_amount: 0,
                    bar_code: item.product.BarCodes?.[0]?.code || '',
                    amount_to_transfer: 0,
                };
            }
            sumItems[productName].standard_amount += item.standard_amount;
            sumItems[productName].stock_amount += item.stock_amount;
        });

        const formatToExport = Object.values(sumItems).map((item) => {
            const amount_to_transfer =
                item.stock_amount > item.standard_amount
                    ? 0
                    : item.standard_amount - item.stock_amount;

            return {
                product_name: item.product_name,
                bar_code: item.bar_code,
                standard_amount: item.standard_amount,
                stock_amount: item.stock_amount,
                amount_to_transfer,
            };
        });

        return formatToExport;
    };

    const exportPlanogramToExcel = async () => {
        setLoadingType('EXCELL');
        try {
            const response = await mutateAsyncExportPlanogram();

            if (response.length <= 0) {
                message.warning('Nenhum planograma encontrado.');
                return;
            }

            const uniqueStoreNames = [
                ...new Set(
                    response.map((item: Planogram) => item?.store?.name),
                ),
            ];
            const storesName = uniqueStoreNames.join(', ');
            const formatToExport = calculateItemsForExport(response);
            exportPlanogramList({
                name: storesName,
                products: formatToExport,
            });
            message.success('Planogramas exportado com sucesso.');
        } catch (e) {
            message.error('Falha ao exportar a lista de planogramas.');
        }
    };

    const exportPlanogramToPdf = async () => {
        setLoadingType('PDF');

        try {
            const response = await mutateAsyncExportPlanogram();

            if (response.length <= 0) {
                message.warning('Nenhum planograma encontrado.');
                return;
            }

            const uniqueStoreNames = [
                ...new Set(
                    response.map((item: Planogram) => item?.store?.name),
                ),
            ];

            const storesName = uniqueStoreNames.join(', ');
            const formatToExport = calculateItemsForExport(response);
            print(formatToExport, {
                name: storesName,
            });
            message.success('Planogramas exportado com sucesso.');
        } catch (e) {
            message.error('Falha ao exportar a lista de planogramas.');
        }
    };

    const enableButton =
        planogramsUpdated.length > 0 || selectedRowKeys.length > 0 || editTable;

    const disableButton =
        !confirmPlanogramChange || planogramsUpdated.length === 0;

    return (
        <Row style={{ padding: '15px 20px' }}>
            <Col xs={24} md={23}>
                <Form form={form} layout="vertical" onFinish={onSubmit}>
                    <Row gutter={[filtersHorizontalGutter, 0]}>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 6 }}
                            style={{
                                paddingLeft: 0,
                                marginLeft: breakpoints.md
                                    ? -(filtersHorizontalGutter / 2)
                                    : 0,
                            }}
                        >
                            <Form.Item label="Loja" name="store">
                                <Space
                                    orientation="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Select
                                        options={storesOptions}
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder="Selecionar Loja..."
                                        showSearch
                                        onChange={(store) =>
                                            setStoresSelected(store)
                                        }
                                        filterOption={filterOption}
                                        disabled={isLoading}
                                    />
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 6 }}
                            style={{ paddingLeft: 0 }}
                        >
                            <Form.Item label="Produto" name="product">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 6 }}
                            style={{ paddingLeft: 0 }}
                        >
                            <Form.Item label="Código de Barras" name="barcode">
                                <Input allowClear maxLength={20} />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 6 }}
                            style={{ paddingLeft: 0 }}
                        >
                            <Form.Item label="Categoria" name="category">
                                <Space
                                    orientation="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Select
                                        options={categoryOptions}
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder="Selecionar uma categoria..."
                                        showSearch
                                        onChange={(category) =>
                                            setCategorySelected(category)
                                        }
                                        filterOption={filterOption}
                                        disabled={isLoading}
                                    />
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ marginTop: 18 }}>
                        <Row wrap gutter={[filtersHorizontalGutter, 8]}>
                            <Col
                                xs={{ span: 24 }}
                                sm={{ span: 24 }}
                                md={{ span: 6 }}
                                style={{
                                    marginBottom: 15,
                                    paddingLeft: 0,
                                    marginLeft: breakpoints.md
                                        ? -(filtersHorizontalGutter / 2)
                                        : 0,
                                }}
                            >
                                <Button
                                    htmlType="submit"
                                    className="default-btn"
                                    icon={<SearchOutlined />}
                                    style={{ width: '100%' }}
                                    loading={isLoadingPlanogram}
                                >
                                    Pesquisar
                                </Button>
                            </Col>
                            <Col
                                xs={{ span: 24 }}
                                sm={{ span: 24 }}
                                md={{ span: 6 }}
                            >
                                <Button
                                    htmlType="reset"
                                    danger
                                    style={{ width: '100%' }}
                                    onClick={() => {
                                        setParams({
                                            ...params,
                                            'filters[barCode]': '',
                                            'filters[product]': '',
                                            'filters[stores][]': [],
                                            'filters[categories][]': [],
                                        });
                                    }}
                                >
                                    Limpar
                                </Button>
                            </Col>
                        </Row>
                    </div>
                    <S.Divider />
                </Form>
            </Col>
            <Col span={24}>
                <Col span={24}>
                    <Row gutter={[10, 10]}>
                        {selectedRowKeys.length > 0 && (
                            <Col
                                style={{
                                    width: breakpoints.md ? undefined : '100%',
                                }}
                            >
                                <Button
                                    type="primary"
                                    ghost
                                    style={{
                                        width: breakpoints.md ? 257 : '100%',
                                    }}
                                    onClick={() => setOpenModal(true)}
                                >
                                    Alterar produtos selecionados
                                </Button>
                            </Col>
                        )}

                        {selectedRowKeys.length <= 0 && !editTable && (
                            <Col
                                style={{
                                    width: breakpoints.md ? undefined : '100%',
                                }}
                            >
                                <Button
                                    type="primary"
                                    className="default-btn"
                                    style={{
                                        width: breakpoints.md ? 227 : '100%',
                                    }}
                                    onClick={() => setEditTable(true)}
                                >
                                    Editar
                                </Button>
                            </Col>
                        )}

                        {!editTable && (
                            <Col
                                style={{
                                    width: breakpoints.md ? undefined : '100%',
                                }}
                            >
                                <Button
                                    type="primary"
                                    ghost
                                    style={{
                                        width: breakpoints.md
                                            ? undefined
                                            : '100%',
                                    }}
                                    onClick={() =>
                                        setOpenModalAddPlanogramFromProduct(
                                            true,
                                        )
                                    }
                                >
                                    Adicionar produto(s) a planograma(s)
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Col>
            </Col>

            <S.TitleItemSelected>
                Itens selecionados: {planogramChecked.length}
            </S.TitleItemSelected>
            <S.StyledRow>
                <S.TitleResult>
                    A pesquisa retornou {meta.total} resultado(s)
                </S.TitleResult>
                <div className="btn-group">
                    <Button
                        icon={<ExportOutlined />}
                        onClick={exportPlanogramToExcel}
                        className="green-btn"
                        loading={
                            isLoadingExportPlanogram && loadingType === 'EXCELL'
                        }
                    >
                        Exportar em Excel
                    </Button>

                    <Button
                        icon={<ExportOutlined />}
                        onClick={exportPlanogramToPdf}
                        className="green-btn"
                        loading={
                            isLoadingExportPlanogram && loadingType === 'PDF'
                        }
                    >
                        Exportar em PDF
                    </Button>
                </div>
            </S.StyledRow>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                }}
            >
                <S.ContainerTables>
                    <PlanogramTableBlock
                        title="Dados de Planograma"
                        columns={planogramColumn}
                        rowSelection={
                            editTable ? rowSelectionFirstTable : undefined
                        }
                        typeTable={PlanogramTableType.PLANOGRAM}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                    />

                    <PlanogramTableBlock
                        title="Dados de Sistema"
                        columns={systemColumn}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.SYSTEM}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />

                    <PlanogramTableBlock
                        title="Métricas Esperadas"
                        columns={expectedMetricsColumn}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.EXPECTED_METRICS}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />

                    <PlanogramTableBlock
                        title="Métricas Atuais"
                        columns={currentMetricsColumns}
                        rowSelection={rowSelection}
                        typeTable={PlanogramTableType.CURRENT_METRICS}
                        dataSource={planogramsCopy}
                        editTable={editTable}
                        rowClassName={rowClassName}
                        selectRow={selectRow}
                        loading={isLoadingPlanogram || isFetching}
                        className="table-to-hide"
                    />
                </S.ContainerTables>
                <div
                    style={{
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 20,
                    }}
                >
                    <Pagination
                        total={meta.total}
                        current={meta.page}
                        onChange={(page, pzSize) => {
                            setCurrentPage(page, pzSize);
                        }}
                    />
                </div>
            </div>

            <div style={{ width: '100%', marginTop: 71 }}>
                <Row
                    align="middle"
                    gutter={[0, 10]}
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Col
                        xs={24}
                        sm={24}
                        md={8}
                        lg={6}
                        xl={6}
                        xxl={4}
                        style={{ height: 32 }}
                    >
                        {enableButton && (
                            <Button
                                htmlType="reset"
                                danger
                                style={{
                                    width: breakpoints.md ? 227 : '100%',
                                }}
                                onClick={() => {
                                    setEditTable(false);
                                    setSelectedRowKeys([]);
                                    setPlanogramsUpdated([]);
                                    setPlanogramsChecked([]);
                                    refetch();
                                }}
                                disabled={!enableButton}
                            >
                                Cancelar
                            </Button>
                        )}
                    </Col>
                    <Col
                        xs={24}
                        sm={24}
                        md={8}
                        lg={6}
                        xl={6}
                        xxl={4}
                        style={{ height: 32 }}
                    >
                        {!!planogramsUpdated.length && (
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                style={{
                                    width: breakpoints.md ? 227 : '100%',
                                }}
                                disabled={disableButton}
                                onClick={() => setOpenModalSavePlanogram(true)}
                                loading={isLoadingUpdatedPlanogram}
                            >
                                {isLoadingUpdatedPlanogram
                                    ? 'Salvando...'
                                    : 'Salvar'}
                            </Button>
                        )}
                    </Col>
                </Row>
                <Form
                    form={formplanogram}
                    layout="vertical"
                    initialValues={{
                        confirm: false,
                        standard_amount_enabled: false,
                    }}
                    style={{ height: '100%' }}
                >
                    <ModalPlanogram
                        open={openModal}
                        setOpenModal={setOpenModal}
                    />
                </Form>
            </div>
            <PlanogramConfirm
                type="save"
                open={openModalSavePlanogram}
                onCancel={() => {
                    setOpenModalSavePlanogram(false);
                    setSelectedRowKeys([]);
                    setPlanogramsUpdated([]);
                    setEditTable(false);
                    refetch();
                }}
                onConfirm={() => onSavePlanogram()}
            />

            <Form
                layout="vertical"
                form={formPlanogramCreate}
                initialValues={{ step: 1 }}
            >
                <ModalAddPlanogramFromProduct
                    open={openModalAddPlanogramFromProduct}
                    onOk={() => setOpenModalAddPlanogramFromProduct(false)}
                    onCancel={() => setOpenModalAddPlanogramFromProduct(false)}
                />
            </Form>
        </Row>
    );
};

export default PlanogramAutomationScreen;
